import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Tetris.css';

const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;

const TETROMINOES = {
  I: { shape: [[1, 1, 1, 1]], color: '#00f0f0' },
  O: { shape: [[1, 1], [1, 1]], color: '#f0f000' },
  T: { shape: [[0, 1, 0], [1, 1, 1]], color: '#a000f0' },
  S: { shape: [[0, 1, 1], [1, 1, 0]], color: '#00f000' },
  Z: { shape: [[1, 1, 0], [0, 1, 1]], color: '#f00000' },
  J: { shape: [[1, 0, 0], [1, 1, 1]], color: '#0000f0' },
  L: { shape: [[0, 0, 1], [1, 1, 1]], color: '#f0a000' }
};

const Tetris = () => {
  const [board, setBoard] = useState(() => 
    Array(BOARD_HEIGHT).fill().map(() => Array(BOARD_WIDTH).fill(0))
  );
  const [currentPiece, setCurrentPiece] = useState(null);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lines, setLines] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);

  const getRandomPiece = () => {
    const pieces = Object.keys(TETROMINOES);
    const randomPiece = pieces[Math.floor(Math.random() * pieces.length)];
    return {
      type: randomPiece,
      shape: TETROMINOES[randomPiece].shape,
      color: TETROMINOES[randomPiece].color,
      x: Math.floor(BOARD_WIDTH / 2) - 1,
      y: 0
    };
  };

  const startGame = () => {
    setBoard(Array(BOARD_HEIGHT).fill().map(() => Array(BOARD_WIDTH).fill(0)));
    setCurrentPiece(getRandomPiece());
    setScore(0);
    setLevel(1);
    setLines(0);
    setGameActive(true);
    setGameOver(false);
  };

  const isValidMove = useCallback((piece, newX, newY, newShape = piece.shape) => {
    for (let y = 0; y < newShape.length; y++) {
      for (let x = 0; x < newShape[y].length; x++) {
        if (newShape[y][x]) {
          const boardX = newX + x;
          const boardY = newY + y;
          
          if (boardX < 0 || boardX >= BOARD_WIDTH || 
              boardY >= BOARD_HEIGHT || 
              (boardY >= 0 && board[boardY][boardX])) {
            return false;
          }
        }
      }
    }
    return true;
  }, [board]);

  const rotatePiece = (shape) => {
    const rotated = shape[0].map((_, index) =>
      shape.map(row => row[index]).reverse()
    );
    return rotated;
  };

  const placePiece = useCallback(() => {
    if (!currentPiece) return;

    const newBoard = board.map(row => [...row]);
    
    // Place the piece on the board
    for (let y = 0; y < currentPiece.shape.length; y++) {
      for (let x = 0; x < currentPiece.shape[y].length; x++) {
        if (currentPiece.shape[y][x]) {
          const boardY = currentPiece.y + y;
          const boardX = currentPiece.x + x;
          if (boardY >= 0) {
            newBoard[boardY][boardX] = currentPiece.color;
          }
        }
      }
    }

    // Check for completed lines
    let linesCleared = 0;
    for (let y = BOARD_HEIGHT - 1; y >= 0; y--) {
      if (newBoard[y].every(cell => cell !== 0)) {
        newBoard.splice(y, 1);
        newBoard.unshift(Array(BOARD_WIDTH).fill(0));
        linesCleared++;
        y++; // Check the same line again
      }
    }

    // Update score and level
    if (linesCleared > 0) {
      const points = [0, 40, 100, 300, 1200][linesCleared] * level;
      setScore(prev => prev + points);
      setLines(prev => prev + linesCleared);
      setLevel(Math.floor(lines / 10) + 1);
    }

    setBoard(newBoard);
    
    // Check game over
    const newPiece = getRandomPiece();
    if (!isValidMove(newPiece, newPiece.x, newPiece.y)) {
      setGameActive(false);
      setGameOver(true);
    } else {
      setCurrentPiece(newPiece);
    }
  }, [currentPiece, board, level, lines, isValidMove]);

  const movePiece = useCallback((dx, dy) => {
    if (!currentPiece || !gameActive) return;

    const newX = currentPiece.x + dx;
    const newY = currentPiece.y + dy;

    if (isValidMove(currentPiece, newX, newY)) {
      setCurrentPiece(prev => ({ ...prev, x: newX, y: newY }));
    } else if (dy > 0) {
      // Piece hit bottom, place it
      placePiece();
    }
  }, [currentPiece, gameActive, placePiece, isValidMove]);

  const handleKeyPress = useCallback((e) => {
    if (!gameActive) return;

    switch (e.key.toLowerCase()) {
      case 'a':
        movePiece(-1, 0);
        break;
      case 'd':
        movePiece(1, 0);
        break;
      case 's':
        movePiece(0, 1);
        break;
      case 'w':
        if (currentPiece) {
          const rotated = rotatePiece(currentPiece.shape);
          if (isValidMove(currentPiece, currentPiece.x, currentPiece.y, rotated)) {
            setCurrentPiece(prev => ({ ...prev, shape: rotated }));
          }
        }
        break;
      default:
        break;
    }
  }, [gameActive, currentPiece, movePiece, isValidMove]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  useEffect(() => {
    if (gameActive) {
      const dropInterval = setInterval(() => {
        movePiece(0, 1);
      }, Math.max(50, 500 - (level - 1) * 50));

      return () => clearInterval(dropInterval);
    }
  }, [gameActive, level, movePiece]);

  const renderBoard = () => {
    const displayBoard = board.map(row => [...row]);
    
    // Add current piece to display
    if (currentPiece) {
      for (let y = 0; y < currentPiece.shape.length; y++) {
        for (let x = 0; x < currentPiece.shape[y].length; x++) {
          if (currentPiece.shape[y][x]) {
            const boardY = currentPiece.y + y;
            const boardX = currentPiece.x + x;
            if (boardY >= 0 && boardY < BOARD_HEIGHT && boardX >= 0 && boardX < BOARD_WIDTH) {
              displayBoard[boardY][boardX] = currentPiece.color;
            }
          }
        }
      }
    }

    return displayBoard.map((row, y) =>
      row.map((cell, x) => (
        <div
          key={`${y}-${x}`}
          className="tetris-cell"
          style={{ backgroundColor: cell || '#333' }}
        />
      ))
    );
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🟦 Tetris</h1>
        <div className="game-instructions">
          Use WASD keys: A/D to move, S to drop faster, W to rotate
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          Score: {score} | Level: {level} | Lines: {lines}
        </div>
        
        <div className="game-play-area">
          <div className="tetris-board">
            {renderBoard()}
          </div>
        </div>

        {gameOver && (
          <div className="game-over">
            <h2>🎮 Game Over!</h2>
            <p>Final Score: {score}</p>
            <p>Lines Cleared: {lines}</p>
          </div>
        )}

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver ? 'Play Again' : 'Start Game'}
          </button>
          <Link to="/" className="btn btn-secondary">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Tetris;